export const COMPANY_ROLES = {
  owner: 'owner',
  admin: 'admin',
  manager: 'manager',
  recruiter: 'recruiter',
  viewer: 'viewer',
};

const ALIASES = {
  COMPANY_OWNER: COMPANY_ROLES.owner,
  OWNER: COMPANY_ROLES.owner,
  COMPANY_ADMIN: COMPANY_ROLES.admin,
  TENANT_ADMIN: COMPANY_ROLES.admin,
  COMPANY_MANAGER: COMPANY_ROLES.manager,
  HIRING_MANAGER: COMPANY_ROLES.manager,
  COMPANY_USER: COMPANY_ROLES.recruiter,
  RECRUITER: COMPANY_ROLES.recruiter,
  USER: COMPANY_ROLES.viewer,
  VIEWER: COMPANY_ROLES.viewer,
};

export const ROLE_META = {
  [COMPANY_ROLES.owner]: {
    label: 'Company Owner',
    shortLabel: 'Owner',
    badgeBg: '#1B3A4B',
    badgeFg: '#FFFFFF',
    description:
      'Can manage users, assign roles, and handle company-level settings including credits, usage and permissions.',
    capabilities: [
      'Manage company credits and usage',
      'Add managers and other users',
      'Assign roles to users',
      'Manage users within the tenant',
      'Access company-level settings',
    ],
    permissions: [
      { title: 'Credits', detail: 'View and grant company credits', allowed: true },
      { title: 'Usage', detail: 'View company usage across products', allowed: true },
      { title: 'Permissions', detail: 'Manage product and feature permissions', allowed: true },
      { title: 'Company settings', detail: 'Company-level controls for this tenant', allowed: true },
      { title: 'User management', detail: 'Add, edit, and remove company users', allowed: true },
    ],
  },
  [COMPANY_ROLES.admin]: {
    label: 'Company Admin',
    shortLabel: 'Admin',
    badgeBg: '#1B3A4B',
    badgeFg: '#FFFFFF',
    description:
      'Can manage users, assign roles, and handle company-level settings including credits, usage and permissions.',
    capabilities: [
      'Manage company credits and usage',
      'Add managers and other users',
      'Assign roles to users',
      'Manage users within the tenant',
      'Access company-level settings',
    ],
    permissions: [
      { title: 'User Management', detail: 'Add, edit, remove users', allowed: true },
      { title: 'Role Assignment', detail: 'Assign roles to users', allowed: true },
      { title: 'Data Access', detail: 'Control data access and visibility', allowed: true },
      { title: 'Company Settings', detail: 'Manage credits, usage, permissions', allowed: true },
    ],
  },
  [COMPANY_ROLES.manager]: {
    label: 'Company Manager',
    shortLabel: 'Manager',
    badgeBg: '#F5C518',
    badgeFg: '#3D2E00',
    description:
      'Can assign roles to individual users, control data accessibility, and perform permitted data-control actions.',
    capabilities: [
      'Add recruiters to their team',
      'Assign roles to users',
      'Control data access and visibility',
      'Manage team members within assigned scope',
      'View reports (limited to assigned data)',
      'Perform permitted data-control actions',
    ],
    permissions: [
      { title: 'User Management', detail: 'Assign roles to individual users', allowed: true },
      { title: 'Data Access', detail: 'Control data access and visibility', allowed: true },
      { title: 'Team Management', detail: 'Manage users within the tenant', allowed: true },
      { title: 'Reports', detail: 'View allowed reports and analytics', allowed: true },
      { title: 'Data Actions', detail: 'Perform permitted data-control actions', allowed: true },
      { title: 'Wizetok', detail: 'Sign in and run interviews in Wizetok', allowed: true },
      { title: 'Company Settings', detail: 'Credits, usage, and permissions', allowed: false },
    ],
  },
  [COMPANY_ROLES.recruiter]: {
    label: 'Recruiter',
    shortLabel: 'Recruiter',
    badgeBg: '#22A06B',
    badgeFg: '#FFFFFF',
    description: 'Can add viewers and access hiring tools allowed by their manager.',
    capabilities: [
      'Add viewers to their team',
      'Work on assigned hiring tools',
      'View and use data permitted by their manager',
    ],
  },
  [COMPANY_ROLES.viewer]: {
    label: 'Viewer',
    shortLabel: 'Viewer',
    badgeBg: '#6B7280',
    badgeFg: '#FFFFFF',
    description: 'Can only access the functionality and data allowed by their recruiter.',
    capabilities: [
      'Access permitted features only',
      'No access to Administration user management',
    ],
    restrictedNote:
      'No access to company settings, user management, role assignments, credits and usage reports.',
  },
};

const PRODUCT_FEATURES = [
  { product: 'reachout', id: 'jd_craft', title: 'JD Craft (AI)', detail: 'AI job description generation. Manual JD write / edit / upload is always available' },
  { product: 'reachout', id: 'stack_ranking', title: 'Stack Ranking', detail: 'View candidate rankings (as permitted)' },
  { product: 'reachout', id: 'outreach', title: 'ReachOut', detail: 'View messages and campaigns (as permitted)' },
  { product: 'reachout', id: 'resume_flow', title: 'Resume Flow', detail: 'Parse and view resumes (as permitted)' },
  { product: 'wizetok', id: 'wizetok', title: 'Wizetok', detail: 'AI interview and assessment platform', standalone: true },
];

export function companyKind(role) {
  if (!role) return COMPANY_ROLES.viewer;
  const key = String(role).trim().toUpperCase().replace(/\s+/g, '_');
  return ALIASES[key] || COMPANY_ROLES.viewer;
}

export function roleMeta(role) {
  return ROLE_META[companyKind(role)];
}

export function roleLabel(role) {
  return roleMeta(role)?.shortLabel || 'User';
}

export function displayName(user) {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  return name || user?.email || 'User';
}

export function initials(user) {
  const name = displayName(user);
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function isOn(value) {
  return value === true || value === 'true' || value === 1 || value === '1';
}

export function featurePermissions(entitlements) {
  return PRODUCT_FEATURES.map((feature) => {
    const bucket = entitlements?.[feature.product];
    const productOn = !!(bucket && isOn(bucket.enabled));
    const featureOn = feature.standalone
      ? productOn
      : productOn && isOn(bucket.features?.[feature.id]);
    return {
      ...feature,
      allowed: featureOn,
      detail: featureOn ? feature.detail : `${feature.title} is not enabled for this company`,
    };
  });
}
